// pages/FAQPage.js
// Frequently Asked Questions accordion
// Commercial use: original IP

import React, { useState } from "react";
import { NAVY, BLUE, STEEL, MUTED, BORDER, BRAND_NAME } from "../constants";
import PageShell from "../components/PageShell";

const faqs = [
  {
    category: "How It Works",
    items: [
      { q: "How do I book a pickup?", a: `Start by taking a photo of your items right in the app. ${BRAND_NAME} reads the photo and figures out your load size for you. Then you pick your item type, confirm your address, choose a date that works, and pay. Most bookings are done in about two minutes.` },
      { q: "Why do I have to upload a photo first?", a: "The photo is what makes fair pricing possible. Our system looks at what you have and assigns the right load size before you ever see a number. That way you are not guessing, and nobody is getting an unfair rate." },
      { q: "Can I pick my own load size?", a: "Your load size is determined by your photo, not by you. We built it that way on purpose. When people choose their own tier they almost always go smaller than they should, which creates problems for loaders and everyone else waiting on a pickup." },
      { q: "How far ahead should I book?", a: "At least 24 hours is ideal. Same day availability depends on how full your zone is that day. If you are moving out or it is PCS season, book as early as you can. Spots go quickly in May and June." },
    ],
  },
  {
    category: "Pricing",
    items: [
      { q: `What does ${BRAND_NAME} cost?`, a: "Pickups start at $75 for a small load and go up from there based on what our system sees in your photo. Your exact price shows up right after your load is classified, before you confirm anything. What you see is what you pay." },
      { q: "Is there an extra charge for mattresses?", a: "Yes. Each mattress adds $15 to your total because of how they have to be handled at the disposal facility. That charge is added automatically when you select mattress as your item type." },
      { q: "Do you take tires?", a: "We do not accept tires. This applies to all tire types and sizes, no exceptions." },
      { q: "When does the charge go through?", a: "Your card is charged when you confirm your booking. The pickup is scheduled once payment clears." },
    ],
  },
  {
    category: "Pickup Day",
    items: [
      { q: "Where should I put my items?", a: "Set everything out in your driveway or your outdoor placement area before your pickup window starts. Items need to be accessible from outside without anyone entering your home. Please keep items out of the street and away from shared community areas." },
      { q: "Is there a weight limit?", a: "Yes. Each individual item needs to be 50 pounds or under. Items heavier than that cannot be loaded safely, so please do not include them in your booking." },
      { q: "What if my loader cannot finish the pickup?", a: "If something comes up that prevents your loader from completing the job, we will reach out to you directly. Whether a refund applies depends on the circumstances, and we handle those situations on a case by case basis." },
      { q: "Will I get a heads up when my loader is coming?", a: "Yes. You will get a text message when your loader is on the way. Just make sure your items are out and ready before your window opens." },
      { q: "Do you pick up cardboard boxes?", a: "Yes. All cardboard needs to be broken down and laid completely flat before your pickup window. Fifteen flat boxes count as one load unit. We cannot load assembled or unflattened boxes, and loaders will leave them behind if they are not broken down." },
    ],
  },
  {
    category: "Eligibility and Access",
    items: [
      { q: `Who can use ${BRAND_NAME} right now?`, a: `${BRAND_NAME} is currently serving residents of Boyer Hill Military Housing at Hill Air Force Base in Utah. We are focused on this community for our launch and will be expanding from here.` },
      { q: "Do I need to do anything to get a loader onto base?", a: "Nothing on your end. Our loaders already have access to Boyer Hill. You do not need to call the gate, sponsor anyone, or arrange anything in advance." },
      { q: `Is ${BRAND_NAME} part of Boyer Hill or the Air Force?`, a: `No. ${BRAND_NAME} Services LLC is an independent private company. We are not affiliated with, endorsed by, or operated by Boyer Hill Military Housing, the Department of Defense, the U.S. Air Force, or any military installation.` },
    ],
  },
  {
    category: "Account and App",
    items: [
      { q: "Is the app on iPhone and Android?", a: `Yes. ${BRAND_NAME} is available on both. Search for ${BRAND_NAME} Services in the App Store or Google Play and you will find us.` },
      { q: "How do I get in touch with support?", a: "Tap the menu inside the app and select Contact Support. We get back to every message within one business day." },
      { q: "What if the app does not accept my photo?", a: "You will see a message telling you what went wrong. Usually it is blur, low light, or a photo that does not clearly show the items. Step outside if you can, get good light on everything, and try again. A clear photo of all your items from a few feet back works best." },
    ],
  },
];

export default function FAQPage() {
  const [openItem, setOpenItem] = useState(null);
  const toggle = (key) => setOpenItem(openItem === key ? null : key);

  return (
    <PageShell style={{ backgroundColor: "#eef1f5", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ backgroundColor: "#071826", padding: "28px 24px 24px" }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2.5, color: "#0487D9", textTransform: "uppercase", marginBottom: 6 }}>Support</div>
        <div style={{ fontWeight: 700, fontSize: 28, color: "#F2F2F2", letterSpacing: 0.3 }}>Frequently Asked Questions</div>
        <div style={{ marginTop: 8, fontSize: 14, color: "#8a9db5", fontWeight: 400, lineHeight: 1.5 }}>Everything you need to know about booking, pricing, and pickup.</div>
      </div>

      {/* Body */}
      <div style={{ padding: "20px 16px 48px" }}>
        {faqs.map((section) => (
          <div key={section.category} style={{ marginBottom: 24 }}>
            {/* Category label */}
            <div style={{ fontWeight: 700, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#0487D9", marginBottom: 10, paddingLeft: 2 }}>{section.category}</div>

            {/* Cards */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {section.items.map((item, i) => {
                const key = `${section.category}-${i}`;
                const isOpen = openItem === key;
                return (
                  <div key={key} onClick={() => toggle(key)} style={{ backgroundColor: "#ffffff", borderRadius: 10, overflow: "hidden", cursor: "pointer", border: isOpen ? "1.5px solid #0487D9" : "1.5px solid transparent", transition: "border-color 0.15s ease" }}>
                    {/* Question row */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px", gap: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#071826", lineHeight: 1.4, flex: 1 }}>{item.q}</div>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: isOpen ? "#0487D9" : "#eef1f5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background-color 0.15s ease" }}>
                        <div style={{ width: 10, height: 2, backgroundColor: isOpen ? "#fff" : "#0487D9", position: "relative", transition: "background-color 0.15s ease" }}>
                          {!isOpen && <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%) rotate(90deg)", width: 10, height: 2, backgroundColor: "#0487D9" }} />}
                        </div>
                      </div>
                    </div>
                    {/* Answer */}
                    {isOpen && (
                      <div style={{ padding: "0 18px 18px", fontSize: 14, color: "#3d4f61", lineHeight: 1.65, fontWeight: 400, borderTop: "1px solid #eef1f5", paddingTop: 14 }}>{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Footer disclaimer */}
        <div style={{ marginTop: 16, padding: 16, backgroundColor: "#dde3ea", borderRadius: 10, fontSize: 11, color: "#6b7f93", lineHeight: 1.6, textAlign: "center" }}>
          {BRAND_NAME} Services LLC is not affiliated with, endorsed by, or operated by Boyer Hill Military Housing, the Department of Defense, the U.S. Air Force, or any military installation.
        </div>
      </div>
    </PageShell>
  );
}
